// frontend/src/components/looks/RepostButton.js
// W3 — Repost a Look. Account required to act; repost count is public.
//
// Props:
//   lookId   — Look._id (required)
//   apiBase  — optional API base override
//   compact  — render the compact icon+label variant (default true)

import React, { useState, useCallback, useEffect } from "react";
import { FaRetweet } from "react-icons/fa";
import AuthRequiredModal from "../AuthRequiredModal";

const DEFAULT_API = process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function RepostButton({ lookId, apiBase = DEFAULT_API, compact = true }) {
  const [reposted, setReposted] = useState(false);
  const [repostCount, setRepostCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showAuth, setShowAuth] = useState(false);

  const getToken = () => localStorage.getItem("ogestyle_token");

  const loadState = useCallback(async () => {
    if (!lookId) return;
    try {
      const headers = {};
      const token = getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${apiBase}/api/looks-social/reposts/${lookId}`, { headers });
      const data = await res.json();
      if (!res.ok) return;
      setRepostCount(Number(data.repostCount) || 0);
      if (token) setReposted(!!data.repostedByMe);
    } catch (err) {
      // Non-fatal.
    }
  }, [lookId, apiBase]);

  useEffect(() => {
    loadState();
  }, [loadState]);

  const toggle = async () => {
    setError("");
    const token = getToken();
    if (!token) {
      setShowAuth(true);
      return;
    }
    setBusy(true);
    try {
      const method = reposted ? "DELETE" : "POST";
      const res = await fetch(`${apiBase}/api/looks-social/repost/${lookId}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.message || "Unable to update repost");
        return;
      }
      const next = !!data.reposted;
      setReposted(next);
      setRepostCount(Number(data.repostCount) || 0);
    } catch (err) {
      setError("Unable to update repost");
    } finally {
      setBusy(false);
    }
  };

  const style = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    border: "none",
    background: "transparent",
    color: reposted ? "#059669" : "#64748b",
    fontSize: compact ? "12px" : "13px",
    fontWeight: 600,
    cursor: busy ? "default" : "pointer",
    padding: "0",
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
  };

  return (
    <>
      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
        <button onClick={toggle} disabled={busy} aria-label={reposted ? "Reposted" : "Repost"} style={style}>
          <FaRetweet size={14} />
          <span>{reposted ? "Reposted" : "Repost"}</span>
          {repostCount > 0 && (
            <span style={{ color: "#9ca3af" }}>{repostCount.toLocaleString()}</span>
          )}
        </button>
        {error && <span style={{ fontSize: "10px", color: "#b91c1c" }}>{error}</span>}
      </span>
      <AuthRequiredModal
        open={showAuth}
        onClose={() => setShowAuth(false)}
        intent="repost this look"
        onAuthed={() => {
          setShowAuth(false);
          toggle();
        }}
      />
    </>
  );
}

export default RepostButton;
